"use client"
import { Box, Divider, Tooltip, Popover, Typography, SxProps, Theme } from "@mui/material";
import { MenuBookOutlined, DoubleArrow, StorageOutlined,
	AdminPanelSettingsOutlined, SpaceDashboardOutlined,
	PeopleAltOutlined, EventNoteOutlined, AssignmentTurnedInOutlined, AccountCircle,
	NotificationsOutlined, Logout
 } from "@mui/icons-material";

import { fetchCategories } from "@lib/category/category-client-service";
import { FC, memo, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { AllRoles } from "@lib/authorizationRoles";
import { AuthorizationContext } from "@components/AuthorizationContext";
import { checkRole } from "@utils/helpers";
import {
	SideNavDrawer,
	sideNavStyles,
	navButtonMainStyle,
	navButtonPopoverStyle,
	navButtonFooterStyle,
	navIconStyle,
	directionalIconStyle,
	sideNavDividerStyles,
	sideNavPopoverPaperStyles,
	sideNavPopoverHeaderStyles,
	sideNavPopoverChildTextStyles,
	sideNavHeaderTextStyles,
	iconBaseStyle,
	navMenuItemStyle
} from "@components/ThemeRegistry/theme";
import NavMenu from "@components/SideNav/NavMenu";
import { ICategory,IMenuItem, PowerBIReportFilterStorage } from "@definitions/index";
import { ROUTE } from "@constants/common";
import { PAGE_NAMES } from "@constants/pages";
import { useTheme } from '@mui/material/styles';
import { saveCategoryToSessionStorage, getPathCategoryReportName } from "@utils/powerBIEntityCategory";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';

import { AuthenticatedTemplate, useAccount, useMsal, } from "@azure/msal-react";

import { usePathname, useRouter } from "next/navigation";

// Pure helper is hoisted out of the component so it isn't recreated on every render.
const get2PartsOfPath = (path: string) => {
	const pathParts = path.split("/");
	let navPath = "";
	if (pathParts.length === 2) {
		navPath = `/${pathParts[1]}`;
	} else if (pathParts.length > 2) {
		navPath = `/${pathParts[1]}/${pathParts[2]}`;
	}
	return navPath;
};

const renderIcon = (icon?: ReactNode | string) => {
	if (!icon) {
		return null;
	}
	if (typeof icon === "string") {
		return (
			<Box
				component='img'
				src={icon}
				alt=''
				sx={{width: '24px', height: '24px'}}
			/>
		);
	}
	return icon;
};

type NavListItemProps = {
	item: IMenuItem;
	open: boolean;
	activateNav: string;
	onNavigate: (e: any, href?: string) => void;
	sx?: SxProps<Theme>;
};

export type NavMenuItemConfig = {
	key: string;
	label: string;
	icon: ReactNode;
	onClick: () => void;
};

// Each row owns its own popover-anchor state so opening one item's flyout
// doesn't force every sibling row in the rail to re-render.
const NavListItem: FC<NavListItemProps> = memo(function NavListItem({ item, open, activateNav, onNavigate, sx }) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const hasChildren = !!item.children && item.children.length > 0;
	const isActive = activateNav.includes(item.id);

	return (
		<ListItem
			disablePadding
			sx={{ display: 'block' }}
		>
			<Tooltip title={item.text} placement='right'>
				<ListItemButton
					disableTouchRipple
					disabled={item.disable ?? false}
					onClick={(e) => {
						if (hasChildren) {
							setAnchorEl(e.currentTarget as HTMLElement);
						} else {
							onNavigate(e, item.href);
						}
					}}
					sx={navButtonMainStyle(isActive)}
				>
					<ListItemIcon sx={navIconStyle(open)}>{renderIcon(item.icon)}</ListItemIcon>
					<ListItemText>
						<Typography sx={[{ opacity: open ? 1 : 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>{item.text}</Typography>
					</ListItemText>
				</ListItemButton>
			</Tooltip>
			{hasChildren && (
				<Popover
					open={Boolean(anchorEl)}
					anchorEl={anchorEl}
					onClose={() => setAnchorEl(null)}
					anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
					transformOrigin={{ vertical: 'top', horizontal: 'left' }}
					slotProps={{
						paper: {
							sx: sideNavPopoverPaperStyles,
						},
					}}>
					<Typography sx={sideNavPopoverHeaderStyles}>
						{item.text}
					</Typography>
					<Divider sx={sideNavDividerStyles} />
					{item.children!.map((child) =>
						child.guardActive && (
							<ListItemButton
								key={child.id}
								disabled={child.disable ?? false}
								onClick={(e) => {
									onNavigate(e, child.href);
									setAnchorEl(null);
								}}
								sx={navButtonPopoverStyle(activateNav.includes(child.id))}>
								<ListItemText
									primary={
										<Typography sx={sideNavPopoverChildTextStyles}>
											{child.text}
										</Typography>
									}
								/>
							</ListItemButton>
						)
					)}
				</Popover>
			)}
		</ListItem>
	);
});
NavListItem.displayName = "NavListItem";

type NavProps = {
	open: boolean;
  	openCallback: (open: boolean) => void;
};

const SideNav: FC<NavProps> = ({open, openCallback: openCallback}) => {
	const router = useRouter();
	const pathname = usePathname();
	const theme = useTheme();
	const authenticatedContext = useContext(AuthorizationContext);
	const isAllRole = checkRole(authenticatedContext, AllRoles);
	const [activateNav, setActivateNav] = useState("");
	const isSetChildMenu = useRef(true);
	const { instance, accounts } = useMsal();
	const account = useAccount(accounts[0] || {});
	const nameRef = useRef("");
	const [accountAnchorEl, setAccountAnchorEl] = useState<HTMLElement | null>(null);

	if (account?.name) {
		nameRef.current = account.name.replace(',', '');
	}

	const checkPermission = useCallback((item: ICategory) => {
		switch (item.name) {
			case "General":
				return false;
			case "Escheatment":
				return !authenticatedContext.isAADTenant && !authenticatedContext.clientServices?.unclaimedPropertyServicesTrackingReports;
			case "Forecasting":
				return !authenticatedContext.isAADTenant && !authenticatedContext.clientServices?.breakageForecastingServices;
			case "Revenue Recognition":
				return !authenticatedContext.isAADTenant && !authenticatedContext.clientServices?.earningsRecognitionServices;
			default:
				return false;
		}
	}, [
		authenticatedContext.clientServices?.unclaimedPropertyServicesTrackingReports,
		authenticatedContext.clientServices?.breakageForecastingServices,
		authenticatedContext.clientServices?.earningsRecognitionServices
	]);

	const homeItem: IMenuItem = {
		id: "home",
		text: "DataNow",
		href: "/",
		icon: "/cc-icon.png",
		guardActive: isAllRole,
	}

	const MenuItems: IMenuItem[] = useMemo(
		() => {
			return [
				{
					id: "dashboard",
					text: PAGE_NAMES.DASHBOARD,
					icon: <SpaceDashboardOutlined />,
					guardActive: isAllRole,
					children: [
						{
							id: "dashboard/escheatment",
							text: PAGE_NAMES.ESCHEATMENT,
							pageName: "Dashboard - Escheatment",
							pageDescription: "",
							href: "/dashboard/escheatment",
							guardActive: true,
							disable: !authenticatedContext.isAADTenant && !authenticatedContext.clientServices?.unclaimedPropertyServicesTrackingReports,
						},
						{
							id: "dashboard/revenue-recognition",
							text: PAGE_NAMES.REVENUE_RECOGNITION,
							pageName: "Dashboard - Revenue Recognition",
							pageDescription: "",
							href: "/dashboard/revenue-recognition",
							guardActive: true,
							disable: !authenticatedContext.isAADTenant && !authenticatedContext.clientServices?.earningsRecognitionServices,
						},
						{
							id: "dashboard/forecasting",
							text: PAGE_NAMES.FORECASTING,
							pageName: "Dashboard - Forecasting",
							pageDescription: "",
							href: "/dashboard/forecasting",
							guardActive: true,
							disable: !authenticatedContext.isAADTenant && !authenticatedContext.clientServices?.breakageForecastingServices,
						},
					],
				},
				{
					id: "report-library",
					text: PAGE_NAMES.REPORT_LIBRARY,
					icon: '/power-bi.png',
					guardActive: isAllRole,
					children: [],
				},
				{
					id: "reviews-and-approvals",
					text: PAGE_NAMES.REVIEWS_AND_APPROVALS,
					pageName: PAGE_NAMES.REVIEWS_AND_APPROVALS,
					pageDescription: "Review and approve requests",
					href: ROUTE.REVIEWS_AND_APPROVALS,
					icon: <AssignmentTurnedInOutlined />,
					guardActive: isAllRole,
				},
				{
					id: "data-management",
					text: PAGE_NAMES.DATA_MANAGEMENT,
					icon: <StorageOutlined />,
					guardActive: isAllRole,
					children: [
						{
							id: "data-management/entity",
							text: PAGE_NAMES.ENTITIES,
							pageName: PAGE_NAMES.ENTITIES,
							pageDescription: "Manage Entities in the system",
							href: "/data-management/entity",
							guardActive: authenticatedContext.isAADTenant,
						},
						{
							id: "data-management/organization",
							text: PAGE_NAMES.ORGANIZATIONS,
							pageName: PAGE_NAMES.ORGANIZATIONS,
							pageDescription: "This page is for managing Organizations",
							href: "/data-management/organizations",
							guardActive: isAllRole,
						},
						{
							id: "data-management/state-profile",
							text: PAGE_NAMES.STATE_PROFILES,
							pageName: PAGE_NAMES.STATE_PROFILES,
							pageDescription: "This page is for managing State Profiles",
							href: "/data-management/state-profile",
							guardActive: authenticatedContext.isAADTenant,
						},
						{
							id: "data-management/data-streams",
							text: PAGE_NAMES.DATA_STREAMS,
							pageName: PAGE_NAMES.DATA_STREAMS,
							pageDescription: "Review and manage Data Streams lorem ipsum dolor sit amet, consectetuer.",
							href: ROUTE.DATA_STREAMS,
							guardActive: authenticatedContext.isAADTenant,
						},
						{
							id: "data-management/data-layouts",
							text: PAGE_NAMES.DATA_LAYOUTS,
							pageName: PAGE_NAMES.DATA_LAYOUTS,
							pageDescription: "Review and manage Data Layouts.",
							href: ROUTE.DATA_LAYOUTS,
							guardActive: authenticatedContext.isAADTenant,
						},
						{
							id: "data-management/transaction-codes",
							text: PAGE_NAMES.TRANSACTION_CODES,
							pageName: PAGE_NAMES.TRANSACTION_CODES,
							pageDescription: "Mapping transaction codes.",
							href: ROUTE.TRANSACTION_CODES,
							guardActive: authenticatedContext.isAADTenant,
						},
						{
							id: "data-management/card-status-codes",
							text: PAGE_NAMES.CARD_STATUS_CODES,
							pageName: PAGE_NAMES.CARD_STATUS_CODES,
							pageDescription: "Map card status codes to standard card status codes",
							href: ROUTE.CARD_STATUS_CODES,
							guardActive: authenticatedContext.isAADTenant,
						},
						{
							id: "data-management/data-transformations",
							text: PAGE_NAMES.DATA_TRANSFORMATIONS_SHORT,
							pageName: PAGE_NAMES.DATA_TRANSFORMATIONS,
							pageDescription: "",
							href: ROUTE.DATA_TRANSFORMATIONS,
							guardActive: authenticatedContext.isAADTenant,
						},
						{
							id: "data-management/data-processing",
							text: PAGE_NAMES.DATA_PROCESSING,
							pageName: PAGE_NAMES.DATA_PROCESSING,
							pageDescription: "",
							href: ROUTE.DATA_PROCESSING,
							guardActive: authenticatedContext.isAADTenant,
						},
						{
							id: "data-management/populations",
							text: PAGE_NAMES.POPULATION_MANAGEMENT,
							pageName: PAGE_NAMES.POPULATION_MANAGEMENT,
							pageDescription: "",
							href: ROUTE.POPULATION_MANAGEMENT,
							guardActive: authenticatedContext.isAADTenant,
						},
						{
							id: "data-management/balance-factors",
							text: PAGE_NAMES.BALANCE_FACTORS,
							pageName: PAGE_NAMES.BALANCE_FACTORS,
							pageDescription: "",
							href: ROUTE.BALANCE_FACTORS,
							guardActive: authenticatedContext.isAADTenant,
						},
					],
				},
				{
					id: "escheatment-activities",
					text: PAGE_NAMES.ESCHEATMENT_ACTIVITIES,
					icon: <EventNoteOutlined />,
					guardActive: isAllRole,
					children: [
						{
							id: 'escheatment-activities/filings-notices',
							text: PAGE_NAMES.FILINGS_AND_NOTICES,
							pageName: PAGE_NAMES.FILINGS_AND_NOTICES,
							pageDescription: "Review and manage filings and notices",
							href: ROUTE.FILINGS_NOTICES,
							guardActive: isAllRole,
							disable: !authenticatedContext.isAADTenant && !authenticatedContext.clientServices?.unclaimedPropertyDueDiligenceNoticesService,
						},
						{
							id: 'escheatment-activities/reimbursements',
							text: PAGE_NAMES.REIMBURSEMENTS,
							pageName: PAGE_NAMES.REIMBURSEMENTS,
							pageDescription: "Review accounts with activity after filing",
							href: ROUTE.REIMBURSEMENTS,
							guardActive: isAllRole,
							disable: !authenticatedContext.isAADTenant && !authenticatedContext.clientServices?.postEscheatCardholderServices,
						},
						{
							id: 'escheatment-activities/simulation',
							text: PAGE_NAMES.SIMULATIONS,
							pageName: PAGE_NAMES.SIMULATIONS,
							pageDescription: "",
							href: ROUTE.SIMULATIONS,
							guardActive: authenticatedContext.isAADAdminRole,
						}
					],
				},
				{
					id: "user-management",
					text: PAGE_NAMES.USER_MANAGEMENT,
					pageName: PAGE_NAMES.USER_MANAGEMENT,
					pageDescription: "This page is for managing users, Pending invites are pinned at the top",
					href: "/user-management",
					icon: <PeopleAltOutlined/>,
					guardActive: authenticatedContext.isAADTenant,
				},
				{
					id: "admin",
					text: PAGE_NAMES.ADMIN,
					icon: <AdminPanelSettingsOutlined />,
					guardActive: authenticatedContext.isAADAdminRole,
					children: [
						{
							id: "admin/powerbi",
							text: PAGE_NAMES.POWER_BI_REPORT_MANAGEMENT,
							pageName: PAGE_NAMES.POWER_BI_REPORT_MANAGEMENT,
							pageDescription: "",
							href: "/admin/powerbi",
							guardActive: authenticatedContext.isAADAdminRole,
						},
						{
							id: "admin/reports-and-roles",
							text: PAGE_NAMES.REPORTS_AND_ROLES,
							pageName: PAGE_NAMES.REPORTS_AND_ROLES,
							pageDescription: "This page is to assign which reports are visible per Role",
							href: "/admin/reports-and-roles",
							guardActive: authenticatedContext.isAADAdminRole,
						},
					],
				},
				{
					id: "glossary",
					text: PAGE_NAMES.GLOSSARY,
					pageName: PAGE_NAMES.GLOSSARY,
					pageDescription: "",
					href: "/glossary",
					icon: <MenuBookOutlined />,
					guardActive: isAllRole,
				},
			];
		},
		// Depend on the individual flags actually read above rather than the whole
		// authenticatedContext object, so an unrelated context change (e.g. a
		// background token refresh) doesn't force the entire menu tree to rebuild.
		[
			isAllRole,
			authenticatedContext.isAADTenant,
			authenticatedContext.isAADAdminRole,
			authenticatedContext.clientServices?.unclaimedPropertyServicesTrackingReports,
			authenticatedContext.clientServices?.earningsRecognitionServices,
			authenticatedContext.clientServices?.breakageForecastingServices,
			authenticatedContext.clientServices?.unclaimedPropertyDueDiligenceNoticesService,
			authenticatedContext.clientServices?.postEscheatCardholderServices,
		]
	);
	const [menuItems, setMenuItems] = useState(MenuItems);

	const mapCategoriesToMenuItems = useCallback((res: ICategory[]) => {
	return res?.map((category: ICategory) => {
		if (category?.name !== undefined && category.pbiEntities && category.pbiEntities.length > 0) {
			const sortedEntities =
				category.pbiEntities.toSorted((a, b) =>
					a.name.localeCompare(b.name)
				);
			return {
				id: `report-library/${getPathCategoryReportName(
					category.name
				)}`,
				text: category.name,
				pageName: `Report Library - ${category.name}`,
				href: `/report-library/${getPathCategoryReportName(
					category.name
				)}/${getPathCategoryReportName(
					sortedEntities[0]?.name
				)}`,
				guardActive: true,
				disable: checkPermission(category),
			};
		}
		return {} as IMenuItem;
	})
	}, []);

	const withReportLibraryChildren = useCallback((children: IMenuItem[]) => {
		return MenuItems.map((item) =>
			item.id === 'report-library' ? { ...item, children } : item
		);
	}, [MenuItems]);

	useEffect(() => {
		if (isSetChildMenu.current && authenticatedContext) {
			const fetchData = async () => {
				const storageCategoriesString = sessionStorage.getItem("categories");
				if (storageCategoriesString !== null) {
					const storageCategories = JSON.parse(
						storageCategoriesString
					) as ICategory[];
					setMenuItems(withReportLibraryChildren(mapCategoriesToMenuItems(storageCategories)));
				}
				else {
					await fetchCategories(authenticatedContext.refreshToken(), (res: ICategory[]) => {
						saveCategoryToSessionStorage(res);
						setMenuItems(withReportLibraryChildren(mapCategoriesToMenuItems(res)));
					}, undefined);
				}
			};
			fetchData();
			isSetChildMenu.current = false;
		}
	}, [authenticatedContext, withReportLibraryChildren, mapCategoriesToMenuItems]);

	// Tracks which item (and, for a child page, its parent) matches the current
	// route so the active row can render its green left border.
	useEffect(() => {
		const navPath = get2PartsOfPath(pathname);
		let foundActivateNavId = "";
		MenuItems.forEach((item) => {
			if (item.href && get2PartsOfPath(item.href) === navPath) {
				foundActivateNavId = item.id;
			} else if (item.children) {
				item.children.forEach((child) => {
					if (child.href && get2PartsOfPath(child.href) === navPath) {
						foundActivateNavId = child.id;
					}
				});
			}
		});
		setActivateNav(foundActivateNavId);
	}, [pathname, MenuItems]);

	// Auto-collapses the drawer once the viewport shrinks past the 'md' breakpoint.
	// Intentionally one-directional (never auto-reopens) and only fires on the
	// crossing itself, not on every shrink while already below it, so a manual
	// reopen at a narrow width doesn't get immediately fought by minor jitter
	// (zoom changes, DPI/monitor changes, browser chrome, etc.).
	// open/openCallback are read from a ref (not effect deps) so the listener is
	// attached once but still sees the current open state, not a mount-time snapshot.
	const collapseBreakpoint = theme.breakpoints.values.md;
	const lastWidthRef = useRef<number | null>(null);
	const openStateRef = useRef({ open, openCallback });
	useEffect(() => {
		openStateRef.current = { open, openCallback };
	}, [open, openCallback]);
	useEffect(() => {
		lastWidthRef.current = window.innerWidth;
		const handleResize = () => {
			const width = window.innerWidth;
			const { open: isOpen, openCallback: setOpen } = openStateRef.current;
			const crossedBelowBreakpoint =
				lastWidthRef.current !== null &&
				lastWidthRef.current >= collapseBreakpoint &&
				width < collapseBreakpoint;
			if (crossedBelowBreakpoint && isOpen) {
				setOpen(!isOpen);
			}
			lastWidthRef.current = width;
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const handleClick = useCallback((e: any, href: any) => {
		if (href === undefined) {
			return;
		}
		authenticatedContext.setCallbackRedirect(() => router.push(href));
		if (!authenticatedContext.unsavedChange) {
			router.push(href);
		}
		if (authenticatedContext.callbackUnSavedChange) {
			authenticatedContext.callbackUnSavedChange();
		}
		e.preventDefault();
	}, [authenticatedContext, router]);

	const handleDrawerOpen = () => {
		openCallback(true);
	};

	const handleDrawerClose = () => {
		openCallback(false);
	};

	const handleLogout = useCallback(() => {
		PowerBIReportFilterStorage.clearFilterStorages();
		if (!authenticatedContext || !authenticatedContext.setCallbackRedirect) {
			// Logout in unauthenticated context
			instance.logoutRedirect({
				account: instance.getActiveAccount(),
				onRedirectNavigate: () => false,
			});
			return;
		}
		authenticatedContext.setCallbackRedirect(() =>
			instance.logoutRedirect({
				account: instance.getActiveAccount(),
				onRedirectNavigate: () => false,
			}));
		if (!authenticatedContext.unsavedChange) {
			instance.logoutRedirect({
				account: instance.getActiveAccount(),
				onRedirectNavigate: () => false,
			});
		}
		if (authenticatedContext.callbackUnSavedChange) {
			authenticatedContext.callbackUnSavedChange();
		}
	}, [authenticatedContext, instance]);

	// Account settings menu contents, kept as a plain array so it's easy to
	// reuse this shape (or a dictionary keyed the same way) for other NavMenu
	// instances later.
	const accountMenuItems: NavMenuItemConfig[] = useMemo(() => [
		{
			key: 'notifications',
			label: 'Notifications',
			icon: <NotificationsOutlined sx={iconBaseStyle} />,
			onClick: () => handleClick({ preventDefault: () => {} }, ROUTE.NOTIFICATIONS),
		},
		{
			key: 'sign-out',
			label: 'Sign Out',
			icon: <Logout sx={iconBaseStyle} />,
			onClick: handleLogout,
		},
	], [handleLogout, handleClick]);

	return (
		<SideNavDrawer variant="permanent" anchor='left' open={open}
			sx={{ "& .MuiDrawer-paper": sideNavStyles }}>
			<List sx={{padding: '0px', margin: '0px'}}>
				{homeItem.guardActive && (
					<NavListItem item={homeItem} open={open} activateNav={activateNav} onNavigate={handleClick} sx={sideNavHeaderTextStyles} />
				)}
			</List>
			<Divider sx={sideNavDividerStyles} />
			<List sx={{flexGrow: 1, padding: '0px', margin: '0px'}}>
				{ menuItems.map((item) => item.guardActive && (
					<NavListItem key={item.id} item={item} open={open} activateNav={activateNav} onNavigate={handleClick} />
				))}
			</List>
			<Divider sx={sideNavDividerStyles} />
			<Box sx={{display: 'flex'}}>
				<AuthenticatedTemplate>
					<ListItemButton
						disableTouchRipple
						onClick={(event) => setAccountAnchorEl(event.currentTarget)}
						sx={[navButtonFooterStyle, !open && { display: 'none' }]}>
						<ListItemIcon sx={navIconStyle(open)}>
							<AccountCircle />
						</ListItemIcon>
						<ListItemText primary={nameRef.current} />
					</ListItemButton>
					<NavMenu
						anchorEl={accountAnchorEl}
						onClose={() => setAccountAnchorEl(null)}>
						{accountMenuItems.map((item) => (
							<MenuItem sx={navMenuItemStyle}
								key={item.key}
								onClick={() => {
									setAccountAnchorEl(null);
									item.onClick();
								}}>
								<ListItemIcon>{item.icon}</ListItemIcon>
								{item.label}
							</MenuItem>
						))}
					</NavMenu>
				</AuthenticatedTemplate>
				<ListItemButton
					disableTouchRipple
					sx={navButtonFooterStyle}>
					<ListItemIcon
						sx={directionalIconStyle(open)}
						onClick={open ? handleDrawerClose : handleDrawerOpen}>
						<DoubleArrow />
					</ListItemIcon>
				</ListItemButton>
			</Box>
      	</SideNavDrawer>
	);
};

export default memo(SideNav);
